import { useQuery } from "@/api/utils/useQuery"
import { HasuraQueries } from "@/api/hasura/queries"
import { QueryResult } from "@/api/utils/types"
import { WarrantsAccountParser } from "@/models/admin/warrantsAccount/warrantsAccount.parser"
import {
  IWarrantsAccount,
  WarrantsAccountState,
} from "@/models/admin/warrantsAccount/warrantsAccount.types"
import {
  Financials_Warrants_Accounts_Bool_Exp,
  Financials_Warrants_Accounts_Order_By,
  GetWarrantsAccountsQuery,
} from "@/api/hasura/generated/graphql"

export interface WarrantsAccountListResult {
  accounts: IWarrantsAccount[]
  totalCount: number
}

/** "All Filters" selections applied to the accounts table. */
export interface WarrantsAccountFilters {
  /** Filter to a single warrants_account_state, or null for any. */
  status: WarrantsAccountState | null
  /** true = paused only, false = not-paused only, null = either. */
  paused: boolean | null
}

class WarrantsAccountServiceClass {
  getAccounts(
    search: string,
    filters: WarrantsAccountFilters,
    page: number,
    pageSize: number,
    orderBy: Financials_Warrants_Accounts_Order_By,
  ): QueryResult<WarrantsAccountListResult> {
    const where: Financials_Warrants_Accounts_Bool_Exp = {
      account_number: { _ilike: `%${search}%` },
    }
    if (filters.status) {
      where.status = { _eq: filters.status }
    }
    if (filters.paused !== null) {
      // Paused accounts have a non-null pause_start.
      where.pause_start = { _is_null: !filters.paused }
    }

    return useQuery({
      ...HasuraQueries.warrants.accounts(
        (page - 1) * pageSize,
        pageSize,
        where,
        orderBy,
      ),
      useRawResponse: true,
      placeholderData: { accounts: [], totalCount: 0 },
      parserFn: (
        data: GetWarrantsAccountsQuery,
      ): WarrantsAccountListResult => ({
        accounts: data.financials_warrants_accounts.map(
          WarrantsAccountParser.toDomain,
        ),
        totalCount:
          data.financials_warrants_accounts_aggregate.aggregate?.count ?? 0,
      }),
    })
  }
}

export const WarrantsAccountService = new WarrantsAccountServiceClass()
